import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  createChart,
  CrosshairMode,
  ColorType,
  MouseEventParams,
} from "lightweight-charts";
import type {
  IChartApi,
  ISeriesApi,
  SeriesDataItemTypeMap,
  BusinessDay,
  Time,
  UTCTimestamp,
} from "lightweight-charts";
import { useWalletKit } from "@mysten/wallet-kit";
import { TransactionBlock } from "@mysten/sui.js/transactions";
import { SuiClient, getFullnodeUrl } from "@mysten/sui.js/client";
import "./EnhancedTradingInterface.css";
import authService, { BACKEND_URL, type CandlestickPoint as BackendCandlestickPoint } from "./authService";
import TradingHistoryPanel from "./components/TradingHistoryPanel";
import "./TradingChart.css";

interface MemeTokenConfig {
  id: string;
  name: string;
  symbol: string;
  description?: string;
  image?: string;
  contractPackageId: string;
  bondingCurveId: string;
  metadataId?: string | null; // Made optional since it might not be extracted
  coinType: string;
  poolCreated?: boolean;
}

interface DailySalesPoint {
  date: string;
  buyVolume: number;
  sellVolume: number;
  netVolume: number;
  buyCount: number;
  sellCount: number;
}

interface EnhancedTradingInterfaceProps {
  token: MemeTokenConfig;
  user?: any;
  userSessionToken?: string | null;
}

const CONFIG_ID =
  "0x9774e359588ead122af1c7e7f64e14ade261cfeecdb5d0eb4a5b3b4c8ab8bd3e";
const POOLS_ID =
  "0x50eb61dd5928cec5ea04711a2e9b72e5237e79e9fbcd2ce3d5469dc8708e0ee2";
const CLOCK_ID = "0x6";
const METADATA_SUI_ID =
  "0x587c29de216efd4219573e08a1f6964d4fa7cb714518c2c8a0f29abfa264327d";

const TXN_EXPLORER_PREFIX = "https://suiscan.xyz/testnet/tx/";
const BASE_PRICE = 10_000n;
const SLOPE = 100n;
const FIXED_POINT_SCALE = 1_000_000_000n;
const ESTIMATED_GAS_SUI = 0.00105;
const ESTIMATED_GAS_MIST = BigInt(Math.round(ESTIMATED_GAS_SUI * 1_000_000_000));
const USE_STATIC_CANDLES = true;

const mistToSuiNumber = (mist: bigint) => Number(mist) / 1_000_000_000;

const formatTokenAmount = (value: number) => {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value) >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(2)}B`;
  }
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2)}M`;
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
};

const timeframeDurations: Record<string, number> = {
  "5m": 5 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "1d": 24 * 60 * 60 * 1000,
  "1w": 7 * 24 * 60 * 60 * 1000,
};

const formatSuiFromNumber = (value?: number | null, decimals = 3) => {
  if (value === null || value === undefined) {
    return `0.${"0".repeat(decimals)}`;
  }
  return (value / 1_000_000_000).toFixed(decimals);
};

const formatCandlePrice = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "--";
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "--";

  if (Math.abs(numeric) >= 1) {
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  }

  if (Math.abs(numeric) >= 0.01) {
    return numeric.toFixed(4);
  }

  return numeric.toFixed(8);
};

const formatVolumeLabel = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "--";
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "--";

  const abs = Math.abs(numeric);
  if (abs >= 1_000_000_000) {
    return `${(numeric / 1_000_000_000).toFixed(2)}B`;
  }
  if (abs >= 1_000_000) {
    return `${(numeric / 1_000_000).toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    return `${(numeric / 1_000).toFixed(2)}K`;
  }

  if (abs >= 1) {
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return numeric.toFixed(4);
};

const formatTooltipTime = (timestampMs: number, timeframe: string) => {
  if (!Number.isFinite(timestampMs) || timestampMs <= 0) {
    return "--";
  }

  const date = new Date(timestampMs);
  if (timeframe === "5m") {
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  if (timeframe === "1h") {
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface BondingCurveInfo {
  totalMinted: bigint;
  feeBps: number;
  progress: number;
  currentSuiBalance: number;
  totalSuiNeeded: number;
  suiNeededForPool: number;
  poolCreated: boolean;
}

interface CandlePoint {
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  time?: string;
  timestamp?: number;
  buyVolume?: number;
  sellVolume?: number;
  direction?: "buy" | "sell" | "neutral";
  highestVolume?: boolean;
}

type ChartBannerState = {
  message: string;
  tone: "info" | "error";
};

const createFallbackCandles = (
  timeframe: string,
  referenceTime: number = Date.now()
): CandlePoint[] => {
  const dataset = premiumCandleSets[timeframe] ?? premiumCandleSets["5m"];
  const durationMs = timeframeDurations[timeframe] ?? timeframeDurations["5m"];
  const start = referenceTime - durationMs * Math.max(dataset.length - 1, 0);

  return dataset.map((candle, index) => {
    const timestamp = start + index * durationMs;
    const totalVolume = Math.max(candle.volume ?? 0, 0);
    const directionalBias = 0.55 + 0.1 * Math.sin(index * 0.9);
    const buyVolume = totalVolume * Math.min(Math.max(directionalBias, 0.15), 0.9);
    const sellVolume = Math.max(totalVolume - buyVolume, 0);

    return {
      ...candle,
      timestamp,
      buyVolume,
      sellVolume,
    };
  });
};

const isValidObjectId = (id: string | null | undefined): id is string =>
  typeof id === "string" && id.startsWith("0x") && id.length === 66;

const premiumCandleSets: Record<string, CandlePoint[]> = {
  "5m": [
    { open: 0.0000162, high: 0.0000164, low: 0.0000160, close: 0.0000163, volume: 5200, time: "00:00" },
    { open: 0.0000163, high: 0.0000166, low: 0.0000161, close: 0.0000165, volume: 6100, time: "00:05" },
    { open: 0.0000165, high: 0.0000169, low: 0.0000163, close: 0.0000168, volume: 7200, time: "00:10" },
    { open: 0.0000168, high: 0.0000171, low: 0.0000166, close: 0.0000170, volume: 8450, time: "00:15" },
    { open: 0.0000170, high: 0.0000174, low: 0.0000169, close: 0.0000172, volume: 9300, time: "00:20" },
    { open: 0.0000172, high: 0.0000175, low: 0.0000170, close: 0.0000173, volume: 9100, time: "00:25" },
    { open: 0.0000173, high: 0.0000178, low: 0.0000171, close: 0.0000176, volume: 10200, time: "00:30" },
    { open: 0.0000176, high: 0.0000179, low: 0.0000174, close: 0.0000177, volume: 9800, time: "00:35" },
    { open: 0.0000177, high: 0.0000180, low: 0.0000175, close: 0.0000176, volume: 9000, time: "00:40" },
    { open: 0.0000176, high: 0.0000178, low: 0.0000173, close: 0.0000174, volume: 8700, time: "00:45" },
    { open: 0.0000174, high: 0.0000176, low: 0.0000171, close: 0.0000172, volume: 8100, time: "00:50" },
    { open: 0.0000172, high: 0.0000174, low: 0.0000169, close: 0.0000170, volume: 7800, time: "00:55" },
    { open: 0.0000170, high: 0.0000171, low: 0.0000166, close: 0.0000168, volume: 7200, time: "01:00" },
    { open: 0.0000168, high: 0.0000169, low: 0.0000164, close: 0.0000165, volume: 6500, time: "01:05" },
    { open: 0.0000165, high: 0.0000166, low: 0.0000162, close: 0.0000163, volume: 6300, time: "01:10" },
    { open: 0.0000163, high: 0.0000164, low: 0.0000159, close: 0.0000161, volume: 5900, time: "01:15" },
    { open: 0.0000161, high: 0.0000162, low: 0.0000157, close: 0.0000159, volume: 5600, time: "01:20" },
    { open: 0.0000159, high: 0.0000160, low: 0.0000155, close: 0.0000157, volume: 5400, time: "01:25" },
    { open: 0.0000157, high: 0.0000158, low: 0.0000153, close: 0.0000155, volume: 5200, time: "01:30" },
    { open: 0.0000155, high: 0.0000156, low: 0.0000151, close: 0.0000153, volume: 5100, time: "01:35" },
  ],
  "1h": [
    { open: 0.000016, high: 0.0000165, low: 0.0000159, close: 0.0000164, volume: 54000, time: "00:00" },
    { open: 0.0000164, high: 0.000017, low: 0.0000162, close: 0.0000168, volume: 61000, time: "01:00" },
    { open: 0.0000168, high: 0.0000175, low: 0.0000167, close: 0.0000172, volume: 72000, time: "02:00" },
    { open: 0.0000172, high: 0.0000178, low: 0.000017, close: 0.0000174, volume: 78500, time: "03:00" },
    { open: 0.0000174, high: 0.0000176, low: 0.0000171, close: 0.0000172, volume: 72000, time: "04:00" },
    { open: 0.0000172, high: 0.0000173, low: 0.0000168, close: 0.0000170, volume: 66000, time: "05:00" },
    { open: 0.0000170, high: 0.0000171, low: 0.0000165, close: 0.0000167, volume: 62000, time: "06:00" },
    { open: 0.0000167, high: 0.0000169, low: 0.0000162, close: 0.0000164, volume: 58000, time: "07:00" },
    { open: 0.0000164, high: 0.0000166, low: 0.000016, close: 0.0000162, volume: 55000, time: "08:00" },
    { open: 0.0000162, high: 0.0000164, low: 0.0000158, close: 0.0000160, volume: 53000, time: "09:00" },
  ],
  "1d": [
    { open: 0.000015, high: 0.0000165, low: 0.0000148, close: 0.0000162, volume: 540000, time: "Day 1" },
    { open: 0.0000162, high: 0.0000172, low: 0.0000159, close: 0.0000170, volume: 610000, time: "Day 2" },
    { open: 0.0000170, high: 0.0000174, low: 0.0000165, close: 0.0000171, volume: 580000, time: "Day 3" },
    { open: 0.0000171, high: 0.0000176, low: 0.0000166, close: 0.0000173, volume: 620000, time: "Day 4" },
    { open: 0.0000173, high: 0.0000177, low: 0.0000169, close: 0.0000170, volume: 590000, time: "Day 5" },
    { open: 0.0000170, high: 0.0000171, low: 0.0000163, close: 0.0000165, volume: 560000, time: "Day 6" },
    { open: 0.0000165, high: 0.0000167, low: 0.0000159, close: 0.0000161, volume: 530000, time: "Day 7" },
  ],
  "1w": [
    { open: 0.000015, high: 0.000018, low: 0.0000146, close: 0.0000172, volume: 3400000, time: "Week 1" },
    { open: 0.0000172, high: 0.0000185, low: 0.0000165, close: 0.0000180, volume: 3800000, time: "Week 2" },
    { open: 0.0000180, high: 0.0000184, low: 0.0000168, close: 0.0000174, volume: 3200000, time: "Week 3" },
    { open: 0.0000174, high: 0.0000176, low: 0.0000159, close: 0.0000163, volume: 2900000, time: "Week 4" },
  ],
};

const EnhancedTradingInterface: React.FC<EnhancedTradingInterfaceProps> = ({
  token,
  user,
  userSessionToken,
}) => {
  const [chartTimeframe, setChartTimeframe] = useState("5m");
  const [chartData, setChartData] = useState<CandlePoint[]>(() => createFallbackCandles("5m"));
  const [priceChange, setPriceChange] = useState({
    value: 0,
    percent: 0,
    isPositive: true,
  });
  const [curveProgress, setCurveProgress] = useState(token.poolCreated ? 100 : 0);
  const [buyAmount, setBuyAmount] = useState("");
  const [sellAmount, setSellAmount] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [txDigest, setTxDigest] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(0.00001523);
  const [marketCap, setMarketCap] = useState(4800);
  const [volume24h, setVolume24h] = useState(1100000);
  const [holders, setHolders] = useState(45);
  const [isGraduated, setIsGraduated] = useState(Boolean(token.poolCreated));
  const [topHolders, setTopHolders] = useState<{ address: string; percentage: string | number }[]>([]);
  const [newComment, setNewComment] = useState("");
  const [activeTab, setActiveTab] = useState<"comments" | "trading">("comments");
  const [comments, setComments] = useState([
    {
      id: 1,
      user: "7KTh...1EEw",
      avatar: "TK",
      text: "LFG POOL TO THE MOON! 🚀🚀🚀",
      timestamp: Date.now() - 2 * 60 * 1000,
    },
    {
      id: 2,
      user: "H6oG...t6m9",
      avatar: "H6",
      text: "500 SUI POOL! LFGGGGG 🔥",
      timestamp: Date.now() - 5 * 60 * 1000,
    },
    {
      id: 3,
      user: "3Z37...F9KZ",
      avatar: "3Z",
      text: "2 SUI POOL",
      timestamp: Date.now() - 7 * 60 * 1000,
    },
  ]);

  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartApiRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const buyVolumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const sellVolumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const lastBarTimestampRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const [chartLoading, setChartLoading] = useState<boolean>(false);
  const [chartError, setChartError] = useState<string | null>(null);
  const [chartBanner, setChartBanner] = useState<ChartBannerState | null>(null);
  const [dailySales, setDailySales] = useState<DailySalesPoint[]>([]);
  const [spikeThreshold, setSpikeThreshold] = useState<number | null>(null);
  const [tooltipData, setTooltipData] = useState<{
    candle: CandlePoint | null;
    timestamp: number | null;
  }>({ candle: null, timestamp: null });

  const latestCandle = useMemo(() => {
    if (!chartData.length) return null;
    return chartData[chartData.length - 1];
  }, [chartData]);

  const displayedCandle = useMemo(() => {
    if (tooltipData.candle) return tooltipData.candle;
    return latestCandle;
  }, [tooltipData.candle, latestCandle]);

  const lastKnownTimestamp = lastBarTimestampRef.current;

  const displayedTimestamp = useMemo(() => {
    if (tooltipData.timestamp) return tooltipData.timestamp;
    if (displayedCandle?.timestamp) return displayedCandle.timestamp;
    return lastKnownTimestamp ?? null;
  }, [tooltipData.timestamp, displayedCandle, lastKnownTimestamp]);

  const premiumTooltip = useMemo(() => {
    if (!displayedCandle) {
      return {
        direction: "neutral" as const,
        icon: "💎",
        sentiment: "Awaiting trades",
        timeLabel: "--",
        closeLabel: "--",
        openLabel: "--",
        highLabel: "--",
        lowLabel: "--",
        deltaLabel: "--",
        deltaPercentLabel: "--",
        buyVolume: 0,
        sellVolume: 0,
        totalVolume: 0,
        buyPercent: 0,
        sellPercent: 0,
      };
    }

    const closePrice = Number(displayedCandle.close ?? displayedCandle.open ?? 0);
    const openPrice = Number(displayedCandle.open ?? closePrice);
    const highPrice = Number(displayedCandle.high ?? Math.max(openPrice, closePrice));
    const lowPrice = Number(displayedCandle.low ?? Math.min(openPrice, closePrice));
    const delta = closePrice - openPrice;
    const deltaPercent = openPrice !== 0 ? (delta / openPrice) * 100 : 0;
    const buyVolume = Number(displayedCandle.buyVolume ?? 0);
    const sellVolume = Number(displayedCandle.sellVolume ?? 0);
    const totalVolumeRaw = buyVolume + sellVolume;
    const totalVolume = totalVolumeRaw > 0
      ? totalVolumeRaw
      : Number(displayedCandle.volume ?? totalVolumeRaw);

    const direction = delta > 0
      ? ("buy" as const)
      : delta < 0
        ? ("sell" as const)
        : buyVolume > sellVolume
          ? ("buy" as const)
          : sellVolume > buyVolume
            ? ("sell" as const)
            : ("neutral" as const);

    const icon = direction === "buy" ? "🚀" : direction === "sell" ? "⚠️" : "💎";
    const sentiment = direction === "buy"
      ? "Bullish momentum"
      : direction === "sell"
        ? "Bearish pressure"
        : "Sideways balance";

    const timeLabel = displayedTimestamp
      ? formatTooltipTime(displayedTimestamp, chartTimeframe)
      : "--";

    const buyPercent = totalVolume > 0
      ? Math.round((buyVolume / totalVolume) * 100)
      : 0;
    const sellPercent = totalVolume > 0
      ? Math.round((sellVolume / totalVolume) * 100)
      : 0;

    return {
      direction,
      icon,
      sentiment,
      timeLabel,
      closeLabel: formatCandlePrice(closePrice),
      openLabel: formatCandlePrice(openPrice),
      highLabel: formatCandlePrice(highPrice),
      lowLabel: formatCandlePrice(lowPrice),
      deltaLabel: `${delta >= 0 ? "+" : "-"}${formatCandlePrice(Math.abs(delta))}`,
      deltaPercentLabel: `${delta >= 0 ? "+" : "-"}${Math.abs(deltaPercent).toFixed(2)}%`,
      buyVolume,
      sellVolume,
      totalVolume,
      buyPercent,
      sellPercent,
    };
  }, [displayedCandle, displayedTimestamp, chartTimeframe]);

  const fetchCandles = useCallback(async () => {
    const fallback = createFallbackCandles(chartTimeframe);

    if (USE_STATIC_CANDLES) {
      setChartLoading(false);
      setChartError(null);
      setChartBanner(null);
      setChartData(fallback);
      return;
    }

    if (!token?.id) {
      setChartData(fallback);
      return;
    }

    setChartLoading(true);
    setChartError(null);

    try {
      const candleResponse = await authService.fetchCandles(
        token.id,
        chartTimeframe,
        180
      );

      if (!candleResponse.candles.length) {
        setChartData(fallback);
        setChartBanner({
          message: "No trades recorded yet. Showing premium preview candles.",
          tone: "info",
        });
      } else {
        setChartBanner(null);
        const mapped = (candleResponse.candles as BackendCandlestickPoint[])
          .map((candle) => {
            const parsedTime = typeof candle.time === "string" ? Date.parse(candle.time) : Number(candle.time);
            const resolvedTimestamp = Number.isFinite(candle.timestamp)
              ? Number(candle.timestamp)
              : Number.isFinite(parsedTime)
                ? parsedTime
                : Date.now();

            const buyVolume = Number(candle.buyVolume ?? candle.volume ?? 0);
            const sellVolume = Number(candle.sellVolume ?? 0);
            const direction: "buy" | "sell" | "neutral" =
              buyVolume > sellVolume ? "buy" : sellVolume > buyVolume ? "sell" : "neutral";

            return {
              open: Number(candle.open),
              high: Number(candle.high),
              low: Number(candle.low),
              close: Number(candle.close),
              volume: Number(candle.volume ?? 0),
              timestamp: resolvedTimestamp,
              buyVolume,
              sellVolume,
              direction,
            } as CandlePoint;
          })
          .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

        const maxVolume = mapped.reduce((acc, candle) => Math.max(acc, candle.volume ?? 0), 0);
        const highlightThreshold = maxVolume * 0.8;

        setSpikeThreshold(maxVolume > 0 ? maxVolume : null);
        setChartData(
          mapped.map((candle) => ({
            ...candle,
            highestVolume: maxVolume > 0 && (candle.volume ?? 0) >= highlightThreshold,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch candlestick data", error);
      setChartError("Unable to load live candle data. Showing premium preview");
      setChartBanner({
        message: "Live candles unavailable. Displaying premium preview.",
        tone: "error",
      });
      setChartData(fallback);
    } finally {
      setChartLoading(false);
    }
  }, [chartTimeframe, token?.id]);

  const fetchDailySales = useCallback(async () => {
    if (!token?.id) {
      setDailySales([]);
      return;
    }

    try {
      const params = new URLSearchParams();
      params.set("limit", "30");
      params.set("order", "desc");

      const response = await fetch(`${BACKEND_URL}/trading/token/${token.id}/daily-sales?${params.toString()}`);
      if (!response.ok) {
        throw new Error("Failed to load daily sales");
      }

      const payload = await response.json();
      if (!payload.success || !Array.isArray(payload.data)) {
        setDailySales([]);
        return;
      }

      setDailySales(payload.data);
    } catch (error) {
      console.error("Unable to load daily sales", error);
      setDailySales([]);
    }
  }, [token?.id]);

  // Load trading history from backend on component mount
  useEffect(() => {
    fetchCandles();
    fetchDailySales();
  }, [fetchCandles, fetchDailySales]);

  // Fetch top holders
  useEffect(() => {
    const fetchTopHolders = async () => {
      if (!token?.id) return;
      try {
        const response = await fetch(`${BACKEND_URL}/tokens/${token.id}/holders?limit=10`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setTopHolders(data.holders.map((h: any) => ({
              address: h.username || `${h.user_wallet.slice(0, 4)}...${h.user_wallet.slice(-4)}`,
              percentage: h.percentage,
              balance: h.balance
            })));
          }
        }
      } catch (error) {
        console.error("Failed to fetch top holders", error);
      }
    };

    fetchTopHolders();
    // Refresh every 30 seconds
    const interval = setInterval(fetchTopHolders, 30000);
    return () => clearInterval(interval);
  }, [token?.id]);

  const reloadCandles = useCallback(async () => {
    await fetchCandles();
  }, [fetchCandles]);

  const forceCandleRefresh = useCallback(() => {
    void fetchCandles();
  }, [fetchCandles]);

  const { currentAccount, signAndExecuteTransactionBlock, isConnected } =
    useWalletKit();
  const [resolvedMetadataId, setResolvedMetadataId] = useState<string | null>(
    isValidObjectId(token.metadataId) ? (token.metadataId as string) : null
  );
  const [bondingInfo, setBondingInfo] = useState<BondingCurveInfo | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const metadataCacheRef = useRef<Map<string, string>>(new Map());
  const metadataResolvingRef = useRef(false);
  const estimatedGasDisplay = ESTIMATED_GAS_SUI.toFixed(4);

  const resolveMetadataId = useCallback(async (): Promise<string | null> => {
    const cacheKey = token.coinType ?? token.contractPackageId ?? null;

    if (cacheKey && metadataCacheRef.current.has(cacheKey)) {
      const cached = metadataCacheRef.current.get(cacheKey)!;
      if (isValidObjectId(cached)) {
        setResolvedMetadataId(cached);
        return cached;
      }
    }

    if (isValidObjectId(token.metadataId)) {
      const safeMetadataId = token.metadataId as string;
      if (cacheKey) {
        metadataCacheRef.current.set(cacheKey, safeMetadataId);
      }
      setResolvedMetadataId(safeMetadataId);
      return safeMetadataId;
    }

    if (!token.coinType) {
      return null;
    }

    if (metadataResolvingRef.current) {
      return resolvedMetadataId;
    }

    metadataResolvingRef.current = true;
    try {
      const client = new SuiClient({ url: getFullnodeUrl("testnet") });
      const coinMetadata = await client.getCoinMetadata({
        coinType: token.coinType,
      });

      if (coinMetadata?.id && isValidObjectId(coinMetadata.id)) {
        const safeMetadataId = coinMetadata.id;
        if (cacheKey) {
          metadataCacheRef.current.set(cacheKey, safeMetadataId);
        }
        setResolvedMetadataId(safeMetadataId);
        return safeMetadataId;
      }

      console.warn(
        "Coin metadata not found via getCoinMetadata",
        token.coinType
      );
      return null;
    } catch (error) {
      console.warn("Failed to resolve metadata via coin metadata:", error);
      return null;
    } finally {
      metadataResolvingRef.current = false;
    }
  }, [
    token.coinType,
    token.contractPackageId,
    token.metadataId,
    resolvedMetadataId,
  ]);

  useEffect(() => {
    if (isValidObjectId(token.metadataId)) {
      const cacheKey = token.coinType ?? token.contractPackageId ?? null;
      const safeMetadataId = token.metadataId as string;
      if (cacheKey) {
        metadataCacheRef.current.set(cacheKey, safeMetadataId);
      }
      setResolvedMetadataId(safeMetadataId);
      return;
    }

    let cancelled = false;
    const primeMetadata = async () => {
      const metadataId = await resolveMetadataId();
      if (!cancelled && isValidObjectId(metadataId)) {
        setResolvedMetadataId(metadataId);
      }
    };

    primeMetadata();

    return () => {
      cancelled = true;
    };
  }, [
    token.metadataId,
    token.coinType,
    token.contractPackageId,
    resolveMetadataId,
  ]);

  useEffect(() => {
    const fetchBondingInfo = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "/api";
        const res = await fetch(`${BACKEND_URL}/bonding-curve/token/${token.id}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.success) {
          setBondingInfo({
            totalMinted: BigInt(data.totalMinted ?? "0"),
            feeBps: Number(data.feeBps ?? 0),
            progress: Number(data.progressPercentage ?? 0), // Use actual progress from backend
            currentSuiBalance: Number(data.currentSuiBalance ?? 0),
            totalSuiNeeded: Number(data.totalSuiNeeded ?? 0),
            suiNeededForPool: Number(data.suiNeededForPool ?? 0),
            poolCreated: Boolean(data.poolCreated),
          });
        }
      } catch (err) {
        console.warn("Unable to fetch bonding curve info", err);
      }
    };

    fetchBondingInfo();
  }, [token.id]);

  const buyQuote = useMemo(() => {
    if (!bondingInfo) return null;
    const suiAmount = Number(buyAmount);
    if (!Number.isFinite(suiAmount) || suiAmount <= 0) return null;

    // Convert SUI to MIST (subtract estimated gas first)
    const suiAmountMist = BigInt(Math.round(suiAmount * 1_000_000_000));

    // Account for fee: if user pays X SUI, actual buying power is X / (1 + fee%)
    const feeMultiplier = 10_000n + BigInt(bondingInfo.feeBps);
    const suiForTokensMist = (suiAmountMist * 10_000n) / feeMultiplier;

    // Calculate current price per token
    const totalMintedWhole = bondingInfo.totalMinted / FIXED_POINT_SCALE;
    const currentPrice = totalMintedWhole * SLOPE + BASE_PRICE;

    // Estimate tokens: this is approximate for linear bonding curve
    // For exact calculation, we'd need to solve the integral, but this is close enough
    const estimatedTokensMist = (suiForTokensMist * FIXED_POINT_SCALE) / currentPrice;
    const estimatedTokens = Number(estimatedTokensMist) / Number(FIXED_POINT_SCALE);

    // Calculate actual cost for these tokens (for verification)
    const tokenAmountScaled = BigInt(Math.round(estimatedTokens * 1_000_000_000));
    const actualCostMist = (tokenAmountScaled * currentPrice) / FIXED_POINT_SCALE;
    const actualFeeMist = (actualCostMist * BigInt(bondingInfo.feeBps)) / 10_000n;
    const actualTotalMist = actualCostMist + actualFeeMist;

    return {
      tokens: estimatedTokens,
      cost: mistToSuiNumber(actualCostMist),
      fee: mistToSuiNumber(actualFeeMist),
      total: mistToSuiNumber(actualTotalMist),
      totalWithGas: mistToSuiNumber(actualTotalMist + ESTIMATED_GAS_MIST),
      suiInput: suiAmount, // Store the original SUI input
    };
  }, [bondingInfo, buyAmount]);

  const sellQuote = useMemo(() => {
    if (!bondingInfo) return null;
    const parsed = Number(sellAmount);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;

    const amountScaled = BigInt(Math.round(parsed * 1_000_000_000));
    if (bondingInfo.totalMinted < amountScaled) return null;

    const startMinted = bondingInfo.totalMinted - amountScaled;
    const startWhole = startMinted / FIXED_POINT_SCALE;
    const startPrice = startWhole * SLOPE + BASE_PRICE;
    const refundMist = (amountScaled * startPrice) / FIXED_POINT_SCALE;
    const feeMist = (refundMist * BigInt(bondingInfo.feeBps)) / 10_000n;
    const netMist = refundMist - feeMist;

    if (netMist <= 0) return null;

    return {
      tokens: parsed,
      gross: mistToSuiNumber(refundMist),
      fee: mistToSuiNumber(feeMist),
      net: mistToSuiNumber(netMist),
      netAfterGas: Math.max(mistToSuiNumber(netMist) - ESTIMATED_GAS_SUI, 0),
    };
  }, [bondingInfo, sellAmount]);

  const isPositivePrice = priceChange.isPositive;

  useEffect(() => {
    if (!bondingInfo) {
      // Don't assume graduated just from token.poolCreated flag
      return;
    }

    // Use actual progress from bonding curve, not poolCreated flag
    const progressValue = bondingInfo.progress || 0;
    const clamped = Math.min(Math.max(progressValue, 0), 100);
    setCurveProgress(clamped);

    // Only graduated if poolCreated is true (which now means threshold was actually reached)
    setIsGraduated(bondingInfo.poolCreated);
  }, [bondingInfo]);

  const raisedSui = formatSuiFromNumber(bondingInfo?.currentSuiBalance);
  const goalSui = formatSuiFromNumber(bondingInfo?.totalSuiNeeded);
  const remainingSui = formatSuiFromNumber(bondingInfo?.suiNeededForPool);

  useEffect(() => {
    const fetchBalance = async () => {
      if (!isConnected || !currentAccount) {
        setWalletBalance(null);
        return;
      }
      try {
        const client = new SuiClient({ url: getFullnodeUrl("testnet") });
        const balanceRes = await client.getBalance({
          owner: currentAccount.address,
          coinType: "0x2::sui::SUI",
        });
        if (balanceRes?.totalBalance !== undefined) {
          const mist =
            typeof balanceRes.totalBalance === "string"
              ? Number(balanceRes.totalBalance)
              : balanceRes.totalBalance;
          setWalletBalance(mist / 1_000_000_000);
        }
      } catch (err) {
        console.error("Unable to fetch wallet balance", err);
      }
    };

    fetchBalance();
  }, [token.id, isConnected, currentAccount?.address]);

  // Static chart – no live updates

  // Update bonding curve progress
  // Chart rendering
  useEffect(() => {
    if (!chartContainerRef.current || chartApiRef.current) return;

    const container = chartContainerRef.current;
    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight,
      layout: {
        background: { type: ColorType.Solid, color: "#0b1220" },
        textColor: "#cbd5f5",
      },
      grid: {
        vertLines: { color: "rgba(148, 163, 184, 0.12)" },
        horzLines: { color: "rgba(148, 163, 184, 0.12)" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: "rgba(16, 185, 129, 0.45)",
          width: 1,
          style: 0,
          labelBackgroundColor: "rgba(14, 165, 233, 0.35)",
        },
        horzLine: {
          color: "rgba(16, 185, 129, 0.45)",
          width: 1,
          style: 0,
          labelBackgroundColor: "rgba(14, 165, 233, 0.35)",
        },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.08, bottom: 0.2 },
      },
      timeScale: {
        borderVisible: false,
        rightOffset: 3,
        barSpacing: 12,
        secondsVisible: false,
      },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#22c55e",
      wickUpColor: "#22c55e",
      borderUpColor: "#22c55e",
      downColor: "#ef4444",
      wickDownColor: "#ef4444",
      borderDownColor: "#ef4444",
      borderVisible: true,
    });

    const buySeries = chart.addHistogramSeries({
      priceScaleId: "volume",
      priceFormat: { type: "volume" },
      color: "rgba(34, 197, 94, 0.65)",
      base: 0,
    });

    const sellSeries = chart.addHistogramSeries({
      priceScaleId: "volume",
      priceFormat: { type: "volume" },
      color: "rgba(239, 68, 68, 0.65)",
      base: 0,
    });

    chart.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
      borderVisible: false,
    });

    chartApiRef.current = chart;
    candleSeriesRef.current = candleSeries;
    buyVolumeSeriesRef.current = buySeries;
    sellVolumeSeriesRef.current = sellSeries;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      chart.applyOptions({
        width: Math.floor(entry.contentRect.width),
        height: Math.floor(entry.contentRect.height),
      });
      chart.timeScale().fitContent();
    });

    resizeObserver.observe(container);
    resizeObserverRef.current = resizeObserver;

    const handleCrosshairMove = (param: MouseEventParams) => {
      if (!chartData.length || !candleSeriesRef.current) {
        setTooltipData({ candle: null, timestamp: null });
        return;
      }

      const seriesPriceData = param.seriesData.get(candleSeriesRef.current) as
        | (SeriesDataItemTypeMap["Candlestick"] & { time: Time })
        | undefined;

      if (!seriesPriceData) {
        setTooltipData({ candle: null, timestamp: null });
        return;
      }

      let timestampMs: number | null = null;
      const timeValue = seriesPriceData.time as Time;

      if (typeof timeValue === "number") {
        timestampMs = timeValue * 1000;
      } else {
        const businessDay = timeValue as BusinessDay;
        const { year, month, day } = businessDay;
        if (
          Number.isFinite(year) &&
          Number.isFinite(month) &&
          Number.isFinite(day)
        ) {
          timestampMs = Date.UTC(year, month - 1, day);
        }
      }

      if (!Number.isFinite(timestampMs ?? NaN)) {
        setTooltipData({ candle: null, timestamp: null });
        return;
      }

      const targetTimestamp = timestampMs as number;
      const candle = chartData.reduce<CandlePoint | null>((closest, current) => {
        if (!current.timestamp) return closest;
        if (!closest) return current;
        const diffCurrent = Math.abs((current.timestamp ?? 0) - targetTimestamp);
        const diffClosest = Math.abs((closest.timestamp ?? 0) - targetTimestamp);
        return diffCurrent < diffClosest ? current : closest;
      }, null);

      setTooltipData({
        candle,
        timestamp: candle?.timestamp ?? targetTimestamp,
      });
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      resizeObserver.disconnect();
      chart.remove();
      chartApiRef.current = null;
      candleSeriesRef.current = null;
      buyVolumeSeriesRef.current = null;
      sellVolumeSeriesRef.current = null;
      resizeObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!chartApiRef.current) return;

    const chart = chartApiRef.current;
    const timeframe = chartTimeframe;

    chart.timeScale().applyOptions({
      timeVisible: timeframe === "5m" || timeframe === "1h",
      secondsVisible: timeframe === "5m",
      rightOffset: timeframe === "5m" ? 3 : 2,
      barSpacing:
        timeframe === "5m" ? 14 : timeframe === "1h" ? 12 : timeframe === "1d" ? 10 : 8,
    });
  }, [chartTimeframe]);

  useEffect(() => {
    if (!chartData.length || !candleSeriesRef.current || !buyVolumeSeriesRef.current || !sellVolumeSeriesRef.current) {
      return;
    }

    const durationMs = timeframeDurations[chartTimeframe] ?? timeframeDurations["5m"];
    const stepSeconds = Math.max(60, Math.round(durationMs / 1000));

    const toTimestampSeconds = (candle: CandlePoint, index: number): UTCTimestamp => {
      if (Number.isFinite(candle.timestamp)) {
        return Math.floor((candle.timestamp as number) / 1000) as UTCTimestamp;
      }

      if (typeof candle.time === "string") {
        const parsed = Date.parse(candle.time);
        if (!Number.isNaN(parsed)) {
          return Math.floor(parsed / 1000) as UTCTimestamp;
        }
      }

      if (typeof candle.time === "number" && Number.isFinite(candle.time)) {
        const numericTime = candle.time <= 1_000_000_000_000 ? candle.time : candle.time / 1000;
        return Math.floor(numericTime) as UTCTimestamp;
      }

      const fallbackBase = Math.floor(Date.now() / 1000) - stepSeconds * (chartData.length - 1);
      return (fallbackBase + index * stepSeconds) as UTCTimestamp;
    };

    const candlestickSeries = chartData.map((candle, index): SeriesDataItemTypeMap["Candlestick"] => {
      const time = toTimestampSeconds(candle, index);
      return {
        time,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      };
    });

    const buyVolumeSeries = chartData.map((candle, index): SeriesDataItemTypeMap["Histogram"] => {
      const time = toTimestampSeconds(candle, index);
      const buyVolume = Math.max(candle.buyVolume ?? 0, 0);
      return {
        time,
        value: buyVolume,
        color: "rgba(34, 197, 94, 0.65)",
        // label?: direction of spike
      };
    });

    const sellVolumeSeries = chartData.map((candle, index): SeriesDataItemTypeMap["Histogram"] => {
      const time = toTimestampSeconds(candle, index);
      const sellVolume = Math.max(candle.sellVolume ?? 0, 0);
      return {
        time,
        value: sellVolume > 0 ? -sellVolume : 0,
        color: "rgba(239, 68, 68, 0.65)",
      };
    });

    candleSeriesRef.current.setData(candlestickSeries);
    buyVolumeSeriesRef.current.setData(buyVolumeSeries);
    sellVolumeSeriesRef.current.setData(sellVolumeSeries);

    if (candlestickSeries.length) {
      const lastCandle = candlestickSeries[candlestickSeries.length - 1];
      const lastTime = lastCandle.time;

      if (typeof lastTime === "number") {
        lastBarTimestampRef.current = lastTime * 1000;
      } else {
        const { year, month, day } = lastTime as BusinessDay;
        if (
          Number.isFinite(year) &&
          Number.isFinite(month) &&
          Number.isFinite(day)
        ) {
          lastBarTimestampRef.current = Date.UTC(year, month - 1, day);
        } else {
          lastBarTimestampRef.current = null;
        }
      }
    } else {
      lastBarTimestampRef.current = null;
    }
    chartApiRef.current?.timeScale().fitContent();
  }, [chartData, chartTimeframe]);

  useEffect(() => {
    if (!chartData.length) {
      setCurrentPrice(0);
      setPriceChange({ value: 0, percent: 0, isPositive: true });
      return;
    }

    const first = chartData[0];
    const last = chartData[chartData.length - 1];
    const delta = last.close - first.close;
    const percent = first.close !== 0 ? (delta / first.close) * 100 : 0;

    setCurrentPrice(last.close);
    setPriceChange({
      value: delta,
      percent: Number.isFinite(percent) ? percent : 0,
      isPositive: delta >= 0,
    });
  }, [chartData]);

  const resolveUsername = useCallback((walletAddress: string) => {
    const shortAddress = `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`;
    const savedProfile = localStorage.getItem(`profile_${walletAddress}`);
    if (!savedProfile) return shortAddress;
    try {
      const profile = JSON.parse(savedProfile);
      return (profile.username ?? shortAddress) || shortAddress;
    } catch (error) {
      console.error("Error parsing profile for username resolution:", error);
      return shortAddress;
    }
  }, []);

  const saveTransactionToBackend = useCallback(async (transactionData: {
    tokenId: number;
    tokenSymbol: string;
    transactionType: "buy" | "sell";
    suiAmount: number;
    tokenAmount: number;
    pricePerToken: number;
    transactionHash: string;
    suiExplorerUrl: string;
  }) => {
    if (!currentAccount?.address) {
      console.warn("Wallet not connected; skipping backend trade recording");
      return null;
    }

    const payload = {
      walletAddress: currentAccount.address,
      username: resolveUsername(currentAccount.address),
      ...transactionData,
    };

    const backendTransaction = await authService.submitTrade(payload);
    if (backendTransaction) {
      console.log("Transaction stored via trading API:", backendTransaction);
    }
    return backendTransaction;
  }, [currentAccount?.address, resolveUsername]);

  const handleBuy = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setTxDigest(null);
    if (!isConnected || !currentAccount) return setStatus("Connect wallet");
    const parsed = parseFloat(buyAmount);
    if (!parsed || parsed <= 0) return setStatus("Enter a valid SUI amount");

    // Get the calculated token amount from buyQuote
    if (!buyQuote) return setStatus("Unable to calculate token amount");
    const tokenAmount = buyQuote.tokens;

    setLoading(true);
    try {
      const amountWithDecimals = Math.round(tokenAmount * 1_000_000_000).toString();
      const txb = new TransactionBlock();
      txb.setGasBudget(100_000_000); // Set explicit gas budget
      const paymentAmount = BigInt(Math.round(parsed * 1_000_000_000));
      const [payment] = txb.splitCoins(txb.gas, [txb.pure(String(paymentAmount))]);

      const metadataCandidate =
        resolvedMetadataId ?? (await resolveMetadataId());

      if (!isValidObjectId(metadataCandidate)) {
        console.error("❌ MEME_TOKEN metadata not found for token:", token.symbol);
        console.error("Token data:", {
          id: token.id,
          contractPackageId: token.contractPackageId,
          metadataId: token.metadataId,
          coinType: token.coinType,
        });

        setStatus(
          "Error: MEME_TOKEN metadata not available. Token may need to be recreated."
        );
        setLoading(false);
        return;
      }

      const memeTokenMetadataId = metadataCandidate as string;

      if (!resolvedMetadataId) {
        setResolvedMetadataId(memeTokenMetadataId);
      }

      txb.moveCall({
        target: `${token.contractPackageId}::bonding_curve::buy_tokens`,
        arguments: [
          txb.object(token.bondingCurveId),
          payment,
          txb.pure(amountWithDecimals),
          txb.object(CONFIG_ID),
          txb.object(POOLS_ID),
          txb.object(CLOCK_ID),
          txb.object(METADATA_SUI_ID), // SUI metadata
          txb.object(memeTokenMetadataId), // MEME_TOKEN metadata
        ],
        typeArguments: [],
      });

      const res = await signAndExecuteTransactionBlock({
        transactionBlock: txb,
        options: { showEffects: true },
      });

      setTxDigest(res.digest);
      setStatus("Bought successfully");

      // Send transaction to backend for database storage
      await saveTransactionToBackend({
        tokenId: parseInt(token.id),
        tokenSymbol: token.symbol,
        transactionType: "buy",
        suiAmount: parsed, // This is the SUI amount user entered
        tokenAmount: tokenAmount, // This is the calculated token amount
        pricePerToken: tokenAmount > 0 ? parsed / tokenAmount : 0,
        transactionHash: res.digest,
        suiExplorerUrl: `${TXN_EXPLORER_PREFIX}${res.digest}`
      });

      setHistoryRefreshKey((prev) => prev + 1);
      await reloadCandles();
    } catch (err: any) {
      setStatus(`Error: ${err.message || String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSell = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setTxDigest(null);
    if (!isConnected || !currentAccount) return setStatus("Connect wallet");
    const parsed = parseFloat(sellAmount);
    if (!parsed || parsed <= 0) return setStatus("Enter a valid sell amount");
    if (!Number.isInteger(parsed)) return setStatus("You can only sell whole number amounts of tokens");

    setLoading(true);
    try {
      const amountWithDecimals = Math.round(parsed * 1_000_000_000).toString();
      const client = new SuiClient({ url: getFullnodeUrl("testnet") });

      const coinsRes = await client.getCoins({
        owner: currentAccount.address,
        coinType: token.coinType,
        limit: 200,
      });

      const coins = coinsRes.data || [];
      if (coins.length === 0) return setStatus(`No ${token.symbol} found`);

      const need = BigInt(amountWithDecimals);
      const totalBalance = coins.reduce(
        (acc, c) => acc + BigInt(c.balance),
        0n
      );
      if (totalBalance < need) return setStatus("Insufficient token balance");

      let target = coins.find((c) => BigInt(c.balance) >= need) || coins[0];
      const others = coins.filter(
        (c) => c.coinObjectId !== target.coinObjectId
      );

      const txb = new TransactionBlock();
      txb.setGasBudget(100_000_000); // Set explicit gas budget
      const targetArg = txb.object(target.coinObjectId);

      if (BigInt(target.balance) < need && others.length > 0) {
        txb.mergeCoins(
          targetArg,
          others.map((o) => txb.object(o.coinObjectId))
        );
      }

      const memeTokenMetadataId =
        resolvedMetadataId ?? (await resolveMetadataId());

      if (!isValidObjectId(memeTokenMetadataId)) {
        console.error("❌ MEME_TOKEN metadata not found for token:", token.symbol);
        console.error("Token data:", {
          id: token.id,
          contractPackageId: token.contractPackageId,
          metadataId: token.metadataId,
          coinType: token.coinType,
        });

        setStatus(
          "Error: MEME_TOKEN metadata not available. Token may need to be recreated."
        );
        setLoading(false);
        return;
      }

      if (!resolvedMetadataId) {
        setResolvedMetadataId(memeTokenMetadataId);
      }

      txb.moveCall({
        target: `${token.contractPackageId}::bonding_curve::sell_tokens`,
        arguments: [
          txb.object(token.bondingCurveId),
          targetArg,
          txb.pure(amountWithDecimals),
          txb.object(CONFIG_ID),
          txb.object(POOLS_ID),
          txb.object(CLOCK_ID),
          txb.object(METADATA_SUI_ID), // SUI metadata
          txb.object(memeTokenMetadataId), // MEME_TOKEN metadata
        ],
        typeArguments: [],
      });

      const res = await signAndExecuteTransactionBlock({
        transactionBlock: txb,
        options: { showEffects: true },
      });

      setTxDigest(res.digest);
      setStatus("Sold successfully");

      const netSui = sellQuote?.netAfterGas ?? sellQuote?.net ?? parsed * currentPrice;
      const resolvedPrice = parsed > 0 ? netSui / parsed : currentPrice;
      await saveTransactionToBackend({
        tokenId: parseInt(token.id),
        tokenSymbol: token.symbol,
        transactionType: "sell",
        suiAmount: netSui,
        tokenAmount: parsed,
        pricePerToken: resolvedPrice,
        transactionHash: res.digest,
        suiExplorerUrl: `${TXN_EXPLORER_PREFIX}${res.digest}`
      });

      setHistoryRefreshKey((prev) => prev + 1);
      forceCandleRefresh();
    } catch (err: any) {
      setStatus(`Error: ${err.message || String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const comment = {
      id: comments.length + 1,
      user: currentAccount?.address
        ? `${currentAccount.address.slice(0, 4)}...${currentAccount.address.slice(-4)}`
        : "Anonymous",
      avatar: currentAccount?.address
        ? currentAccount.address.slice(0, 2).toUpperCase()
        : "AN",
      text: newComment,
      timestamp: Date.now(),
    };

    setComments((prev) => [comment, ...prev]);
    setNewComment("");
  };

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes} min ago`;
    return "Just now";
  };

  // Save transaction to backend database
  return (
    <div className="enhanced-trading-container">
      <div className="enhanced-trading-wrapper">
        {/* Header Section */}
        <div className="enhanced-trading-header">
          <img
            src={token.image || "/placeholder-coin.png"}
            alt={token.symbol}
            className="enhanced-coin-image"
          />
          <div>
            <h1 className="enhanced-coin-title">
              {token.name} ({token.symbol})
            </h1>
            <p className="enhanced-coin-description">
              {token.description || "No description available"}
            </p>
          </div>
        </div>

        <div className="enhanced-trading-grid">
          {/* Left Column - Chart and Stats */}
          <div className="enhanced-main-column">
            {/* Market Stats */}
            <div className="enhanced-stats-grid">
              <div className="enhanced-stat-card">
                <div className="enhanced-stat-label">MARKET CAP</div>
                <div className="enhanced-stat-value positive">
                  ${marketCap.toLocaleString()}
                </div>
              </div>
              <div className="enhanced-stat-card">
                <div className="enhanced-stat-label">24H VOLUME</div>
                <div className="enhanced-stat-value">
                  ${(volume24h / 1000).toFixed(1)}K
                </div>
              </div>
              <div className="enhanced-stat-card">
                <div className="enhanced-stat-label">HOLDERS</div>
                <div className="enhanced-stat-value">{holders}</div>
              </div>
              <div className="enhanced-stat-card">
                <div className="enhanced-stat-label">PRICE</div>
                <div className="enhanced-stat-value">
                  ${currentPrice.toFixed(8)}
                </div>
              </div>
            </div>

            {/* Price Chart */}
            <div className="enhanced-chart-container">
              <div className="enhanced-chart-header">
                <div className="enhanced-price-display">
                  <div
                    className={`enhanced-current-price ${priceChange.isPositive ? "positive" : "negative"
                      }`}
                    style={{
                      color: priceChange.isPositive ? "#10b981" : "#ef4444",
                    }}
                  >
                    ${currentPrice.toFixed(8)}
                  </div>
                  <div
                    className="enhanced-price-change"
                    style={{
                      color: priceChange.isPositive ? "#10b981" : "#ef4444",
                    }}
                  >
                    <span>
                      {priceChange.isPositive ? "+" : "-"}$
                      {Math.abs(priceChange.value).toFixed(8)}
                    </span>
                    <span>
                      ({priceChange.isPositive ? "+" : "-"}
                      {priceChange.percent.toFixed(2)}%)
                    </span>
                    <span>{priceChange.isPositive ? "↗" : "↘"}</span>
                  </div>
                </div>

                <div className="enhanced-timeframe-buttons">
                  {["5m", "1h", "1d", "1w"].map((timeframe) => (
                    <button
                      key={timeframe}
                      onClick={() => setChartTimeframe(timeframe)}
                      className={`enhanced-timeframe-button ${chartTimeframe === timeframe ? "active" : ""
                        }`}
                    >
                      {timeframe.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div
                ref={chartContainerRef}
                className="enhanced-chart-canvas"
              />
              {displayedCandle && (
                <div className="premium-chart-overlay">
                  <div className={`premium-tooltip direction-${premiumTooltip.direction}`}>
                    <div className="premium-tooltip-header">
                      <span className="tooltip-icon">{premiumTooltip.icon}</span>
                      <div className="tooltip-meta">
                        <span className="tooltip-time">{premiumTooltip.timeLabel}</span>
                        <span className="tooltip-sentiment">{premiumTooltip.sentiment}</span>
                      </div>
                      <div className="tooltip-delta">
                        <span className="tooltip-delta-raw">{premiumTooltip.deltaLabel}</span>
                        <span className="tooltip-delta-percent">{premiumTooltip.deltaPercentLabel}</span>
                      </div>
                    </div>

                    <div className="premium-tooltip-body">
                      <div className="tooltip-price-grid">
                        <div>
                          <span className="tooltip-label">Open</span>
                          <span className="tooltip-value">{premiumTooltip.openLabel}</span>
                        </div>
                        <div>
                          <span className="tooltip-label">High</span>
                          <span className="tooltip-value">{premiumTooltip.highLabel}</span>
                        </div>
                        <div>
                          <span className="tooltip-label">Low</span>
                          <span className="tooltip-value">{premiumTooltip.lowLabel}</span>
                        </div>
                        <div>
                          <span className="tooltip-label">Close</span>
                          <span className="tooltip-value">{premiumTooltip.closeLabel}</span>
                        </div>
                      </div>

                      <div className="tooltip-volume">
                        <div className="volume-bar">
                          <div
                            className="volume-segment buy"
                            style={{ width: `${premiumTooltip.buyPercent}%` }}
                          >
                            {premiumTooltip.buyPercent > 4 && (
                              <span>{premiumTooltip.buyPercent}% BUY</span>
                            )}
                          </div>
                          <div
                            className="volume-segment sell"
                            style={{ width: `${premiumTooltip.sellPercent}%` }}
                          >
                            {premiumTooltip.sellPercent > 4 && (
                              <span>{premiumTooltip.sellPercent}% SELL</span>
                            )}
                          </div>
                        </div>
                        <div className="volume-stats">
                          <span className="vol-buy">Buy {formatVolumeLabel(premiumTooltip.buyVolume)}</span>
                          <span className="vol-total">Total {formatVolumeLabel(premiumTooltip.totalVolume)}</span>
                          <span className="vol-sell">Sell {formatVolumeLabel(premiumTooltip.sellVolume)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bonding Curve Progress */}
            <div className="enhanced-bonding-curve">
              <div className="bonding-progress-header">
                <h3>{isGraduated ? "Premium Level" : "Bonding Curve Progress"}</h3>
                <span className={`bonding-progress-value ${isGraduated ? "complete" : ""}`}>
                  {isGraduated ? "Unlocked" : `${curveProgress.toFixed(1)}%`}
                </span>
              </div>

              {isGraduated ? (
                <div className="premium-dex-card">
                  <div className="premium-dex-badge">
                    <span className="badge-icon">🌐</span>
                    Cross-Chain Liquidity Live
                  </div>
                  <p className="premium-dex-copy">
                    This coin has reached premium level and graduated from the bonding curve.
                    Liquidity is now routed through decentralized exchanges.
                  </p>
                  <a
                    className="premium-dex-link"
                    href="http://suivision.xyz/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Suivision DEX ↗
                  </a>
                </div>
              ) : (
                <>
                  <div className="bonding-progress-bar">
                    <div className="bonding-progress-track"></div>
                    <div
                      className="bonding-progress-fill"
                      style={{ width: `${Math.min(curveProgress, 100)}%` }}
                    ></div>
                    <div
                      className={`bonding-progress-rocket ${curveProgress >= 80 ? "sparkle-active" : ""}`}
                      style={{ left: `${Math.min(curveProgress, 98)}%` }}
                    >
                      <div className="rocket-image-container">
                        <img
                          src="/image.png"
                          alt="Rocket"
                          className="rocket-image"
                        />
                      </div>
                      <div className="particle-system">
                        <div className="particle particle-1"></div>
                        <div className="particle particle-2"></div>
                        <div className="particle particle-3"></div>
                        <div className="particle particle-4"></div>
                        <div className="particle particle-5"></div>
                        <div className="particle particle-6"></div>
                      </div>
                      <div className="speed-lines">
                        <div className="speed-line line-1"></div>
                        <div className="speed-line line-2"></div>
                        <div className="speed-line line-3"></div>
                      </div>
                      <div className="rocket-glow"></div>
                      <div className="sparkle-trail"></div>
                      <div className="sparkle-particle p1"></div>
                      <div className="sparkle-particle p2"></div>
                      <div className="sparkle-particle p3"></div>
                      <div className="sparkle-particle p4"></div>
                      <div className="sparkle-particle p5"></div>
                      <div className="sparkle-particle p6"></div>
                    </div>
                  </div>

                  <div className="bonding-progress-stats">
                    <div className="bonding-progress-stat">
                      <span className="label">Raised</span>
                      <strong>{raisedSui} SUI</strong>
                    </div>
                    <div className="bonding-progress-stat">
                      <span className="label">Goal</span>
                      <strong>{goalSui} SUI</strong>
                    </div>
                    <div className="bonding-progress-stat">
                      <span className="label">To Pool</span>
                      <strong>{remainingSui} SUI</strong>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Comments & Trading History Section */}
            <div className="enhanced-activity-section">
              <div className="enhanced-activity-tabs">
                <button
                  className={`enhanced-activity-tab ${activeTab === "comments" ? "active" : ""}`}
                  onClick={() => setActiveTab("comments")}
                >
                  💬 Comments
                </button>
                <button
                  className={`enhanced-activity-tab ${activeTab === "trading" ? "active" : ""}`}
                  onClick={() => setActiveTab("trading")}
                >
                  📊 Trading History
                </button>
              </div>

              <div className="enhanced-activity-content">
                {activeTab === "comments" && (
                  <>
                    <div className="enhanced-comments-list">
                      {comments.map((comment) => (
                        <div key={comment.id} className="enhanced-comment-item">
                          <div className="enhanced-comment-avatar">
                            {comment.avatar}
                          </div>
                          <div className="enhanced-comment-content">
                            <div className="enhanced-comment-header">
                              <span className="enhanced-comment-user">
                                {comment.user}
                              </span>
                              <span className="enhanced-comment-time">
                                {formatTimeAgo(comment.timestamp)}
                              </span>
                            </div>
                            <div className="enhanced-comment-text">
                              {comment.text}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Comment Input */}
                    <div className="enhanced-comment-input-section">
                      <form onSubmit={handleCommentSubmit}>
                        <textarea
                          className="enhanced-comment-input"
                          placeholder="Share your thoughts..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          disabled={!isConnected}
                        />
                        <button
                          type="submit"
                          className="enhanced-comment-submit"
                          disabled={!isConnected || !newComment.trim()}
                        >
                          {isConnected ? "Post Comment" : "Connect Wallet to Comment"}
                        </button>
                      </form>
                    </div>
                  </>
                )}

                {activeTab === "trading" && (
                  <TradingHistoryPanel
                    tokenId={token.id}
                    defaultTokenSymbol={token.symbol}
                    refreshSignal={historyRefreshKey}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Trading and Top Holders */}
          <div className="enhanced-right-column">
            <div className={`enhanced-trading-card ${isGraduated ? "premium-locked" : ""}`}>
              <h3 className="enhanced-trading-title">Trade {token.symbol}</h3>

              {isGraduated ? (
                <div className="premium-boost-panel">
                  <div className="premium-boost-heading">
                    <div className="premium-boost-label">Premium Level</div>
                    <div className="premium-boost-status">Unlocked</div>
                  </div>
                  <div className="premium-boost-body">
                    <div className="premium-boost-live">
                      <span className="premium-boost-live-icon" aria-hidden>🌐</span>
                      <span className="premium-boost-title">Cross-Chain Liquidity Live</span>
                    </div>
                    <p className="premium-boost-copy">
                      This coin has reached premium level and graduated from the bonding curve. Liquidity
                      now flows through decentralized exchanges with Suivision orchestration.
                    </p>
                    <a
                      className="premium-boost-link"
                      href="http://suivision.xyz/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Suivision DEX ↗
                    </a>
                  </div>
                </div>
              ) : (
                <div className="enhanced-trading-sections">
                  {/* Buy Section */}
                  <div className="enhanced-buy-section">
                    <h4 className="enhanced-section-title buy">
                      Buy {token.symbol}
                    </h4>
                    <div className="enhanced-balance-bar">
                      <div className="balance-meta">
                        <span className="balance-caption">Balance</span>
                        <span className="balance-value">
                          {walletBalance !== null ? `${walletBalance.toFixed(4)} SUI` : "-- SUI"}
                        </span>
                      </div>
                      <div className="balance-token-pill">
                        <img src="/suiicon.png" alt="Sui" className="token-icon" loading="lazy" />
                        <span className="token-symbol">SUI</span>
                      </div>
                    </div>
                    <form onSubmit={handleBuy}>
                      <input
                        type="text"
                        placeholder="Amount in SUI"
                        value={buyAmount}
                        onChange={(e) => setBuyAmount(e.target.value)}
                        disabled={loading}
                        className="enhanced-trade-input"
                      />
                      {buyQuote && (
                        <div className="enhanced-trade-quote buy">
                          <div className="quote-gas-pulse">
                            Gas needed: {estimatedGasDisplay} SUI
                          </div>
                          <div className="quote-total">
                            {buyQuote.total.toFixed(4)} SUI
                          </div>
                          <div className="quote-note">
                            <span>You will receive: {formatTokenAmount(buyQuote.tokens)} {token.symbol}</span>
                          </div>
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={loading || !isConnected}
                        className="enhanced-trade-button buy"
                      >
                        {loading ? "Processing..." : `Buy ${token.symbol}`}
                      </button>
                    </form>
                  </div>

                  {/* Sell Section */}
                  <div className="enhanced-sell-section">
                    <h4 className="enhanced-section-title sell">
                      Sell {token.symbol}
                    </h4>
                    <form onSubmit={handleSell}>
                      <input
                        type="text"
                        placeholder={`Amount in ${token.symbol}`}
                        value={sellAmount}
                        onChange={(e) => setSellAmount(e.target.value)}
                        disabled={loading}
                        className="enhanced-trade-input"
                      />
                      {sellQuote && (
                        <div className="enhanced-trade-quote sell">
                          <div className="quote-gas-pulse">
                            Gas needed: {estimatedGasDisplay} SUI
                          </div>
                          <div className="quote-total">
                            {sellQuote.netAfterGas.toFixed(4)} SUI
                          </div>
                          <div className="quote-note">
                            <span>{formatTokenAmount(sellQuote.tokens)} {token.symbol}</span>
                          </div>
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={loading || !isConnected}
                        className="enhanced-trade-button sell"
                      >
                        {loading ? "Processing..." : `Sell ${token.symbol}`}
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </div>

            {/* Status Messages */}
            {status && (
              <div
                className={`enhanced-status-message ${status.includes("Error") ? "error" : "success"
                  }`}
              >
                {status}
              </div>
            )}

            {txDigest && (
              <div className="enhanced-transaction-link">
                <a
                  href={`${TXN_EXPLORER_PREFIX}${txDigest}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Transaction: {txDigest.substring(0, 10)}...
                </a>
              </div>
            )}

            {/* Top Holders */}
            <div className="enhanced-holders-card">
              <h3 className="enhanced-holders-title">Top Holders</h3>
              <div className="enhanced-holders-list">
                {topHolders.map((holder, index) => (
                  <div key={index} className="enhanced-holder-item">
                    <span className="enhanced-holder-address">
                      {holder.address}
                    </span>
                    <span className="enhanced-holder-percentage">
                      {holder.percentage}%
                    </span>
                  </div>
                ))}
              </div>

              <button className="enhanced-bubble-map-button">
                Generate bubble map
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedTradingInterface;
